import { useQuery } from "@tanstack/react-query";
import { useAuth } from "@/providers/AuthProvider";
import { getDashboardStats } from "@/api/dashboard";
import { RecentAssessments } from "./dashboard/RecentAssessments";
import { Skeleton } from "@/components/ui/skeleton";
import { StatCard } from "./dashboard/StatCard";
import { PageContent } from "./ui/page-content";
import { PageHeading } from "./ui/page-heading";
import moment from "moment";
import { Card, CardHeader, CardTitle, CardContent } from "./ui/card";
import { ActivityItem } from "@/types";

export function Dashboard() {
  const { activeTeam } = useAuth();

  const { data: stats, isLoading } = useQuery({
    queryKey: ["dashboard", activeTeam?.id],
    queryFn: () =>
      activeTeam ? getDashboardStats(activeTeam.id) : Promise.resolve(null),
    enabled: !!activeTeam,
  });

  const recentActivity: ActivityItem[] = [
    {
      user: "John Smith",
      action: "completed",
      assessment: "Senior Frontend Developer",
      score: "92%",
      time: moment().subtract(2, "hours").toISOString(),
    },
    {
      user: "Sarah Johnson",
      action: "started",
      assessment: "DevOps Engineer",
      time: moment().subtract(3, "hours").toISOString(),
    },
    {
      user: "Michael Brown",
      action: "invited",
      assessment: "UI/UX Designer",
      time: moment().subtract(4, "hours").toISOString(),
    },
  ];

  return (
    <>
      <PageHeading>Dashboard</PageHeading>
      <PageContent>
        {isLoading && (
          <>
            <div className="space-y-6">
              <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-4">
                {Array(4)
                  .fill(0)
                  .map((_, i) => (
                    <Skeleton key={i} className="h-32" />
                  ))}
              </div>
              <Skeleton className="h-[400px]" />
            </div>
          </>
        )}

        {!isLoading && stats && (
          <>
            {/* Stats Grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
              <StatCard
                title="Total assessments"
                value={stats.total_assessments.count}
                change={stats.total_assessments.increase_this_month}
                unit=""
              />
              <StatCard
                title="Active candidates"
                value={stats.active_candidates.count}
                change={stats.active_candidates.increase_this_week}
                unit=""
              />
              <StatCard
                title="Average score"
                value={stats.average_score.score}
                change={stats.average_score.percentage_change_last_week}
                unit="%"
              />
              <StatCard
                title="Completion rate"
                value={stats.completion_rate.rate}
                change={stats.completion_rate.percentage_change_last_week}
                unit="%"
              />
            </div>
            {/* Recent Assessments and Activity */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-y-6 lg:gap-6">
              <div className="col-span-2">
                <RecentAssessments assessments={stats.recent_assessments} />
              </div>

              {/* Recent Activity */}
              <Card>
                <CardHeader>
                  <CardTitle>Recent activity</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {recentActivity.map((activity, index) => (
                      <div key={index} className="flex items-center space-x-4">
                        <div className="w-8 h-8 rounded-full bg-purple-100 text-purple-500 flex items-center justify-center font-bold">
                          {activity.user.charAt(0)}
                        </div>
                        <div className="flex-1 space-y-0">
                          <p>
                            <span className="font-bold">{activity.user}</span>{" "}
                            {activity.action}{" "}
                            <span className="text-purple-500">
                              {activity.assessment}
                            </span>
                            {activity.score && (
                              <span className="text-green-500">
                                {" "}
                                with {activity.score}
                              </span>
                            )}
                          </p>
                          <p className="text-sm text-muted-foreground">
                            {moment(activity.time).fromNow()}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </div>
          </>
        )}
      </PageContent>
    </>
  );
}
